// OPT-336: the Shopify connect flow's small helpers (install link, the ticket kept across sign-in,
// the copy for each refusal). The ticket is a bearer of the shop's identity for 15 minutes; it is
// never logged, and it lives in sessionStorage only while a sign-in is in the way.
const PENDING_KEY = "sellary.shopify.pendingTicket";
const PENDING_MAX_MS = 15 * 60 * 1000;

/** The app's install link for this build, or null when none is configured (Shopify is then not offered). */
export function shopifyInstallUrl(env = process.env) {
  const url = env.VUE_APP_SHOPIFY_INSTALL_URL;
  return typeof url === "string" && /^https:\/\/\S+$/.test(url.trim()) ? url.trim() : null;
}

/** OPT-378: the app's handle in the Shopify admin (set per Hosting branch), or null when not configured. */
export function shopifyAppHandle(env = process.env) {
  const h = env.VUE_APP_SHOPIFY_APP_HANDLE;
  return typeof h === "string" && /^[a-z0-9][a-z0-9-]*$/i.test(h.trim()) ? h.trim() : null;
}

/** OPT-378: where a seller opens Sellary inside their Shopify admin; the store's app list until a handle is set. */
export function shopifyAdminAppUrl(shop, env = process.env) {
  const store = String(shop || "").trim().toLowerCase().replace(/\.myshopify\.com$/, "");
  if (!/^[a-z0-9][a-z0-9-]*$/.test(store)) return null;
  const handle = shopifyAppHandle(env);
  return `https://admin.shopify.com/store/${store}/apps${handle ? `/${handle}` : ""}`;
}

export function savePendingTicket(ticket, now = Date.now()) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ticket, savedAt: now }));
  } catch (e) {
    // Storage blocked: the claim still works from the address bar's copy for this page load.
  }
}

export function clearPendingTicket() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // ignore
  }
}

/** The pending ticket, or null when absent, unreadable or older than 15 minutes (which clears it). */
export function readPendingTicket(now = Date.now()) {
  let raw;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    clearPendingTicket();
    return null;
  }
  if (!parsed || typeof parsed.ticket !== "string" || !parsed.ticket || typeof parsed.savedAt !== "number" || now - parsed.savedAt > PENDING_MAX_MS) {
    clearPendingTicket();
    return null;
  }
  return parsed.ticket;
}

/** The shop named in a ticket's payload, for display only (the server verifies the ticket). */
export function ticketShop(ticket) {
  try {
    const m = /^v1\.([A-Za-z0-9_-]+)\./.exec(String(ticket));
    if (!m) return null;
    const b64 = m[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = typeof atob === "function" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
    const payload = JSON.parse(json);
    return payload && typeof payload.shop === "string" && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(payload.shop) ? payload.shop : null;
  } catch (e) {
    return null;
  }
}

/**
 * Router hook: a navigation to /connect/shopify carrying ?ticket= is stored and redirected to the
 * bare path, so the ticket never reaches the route logger, history or the page. Returns the
 * location to redirect to, or null when the navigation is not such a link.
 */
export function stripTicketRedirect(to) {
  if (!to || to.path !== "/connect/shopify" || !to.query || typeof to.query.ticket !== "string" || !to.query.ticket) return null;
  savePendingTicket(to.query.ticket);
  return { path: "/connect/shopify", replace: true };
}

/** After a sign-in or sign-up: if a ticket is waiting, go back to the claim. Returns whether it did. */
export function resumePendingConnect(router) {
  if (!readPendingTicket()) return false;
  router.replace("/connect/shopify").catch(() => {});
  return true;
}

const OPEN_AGAIN = "Open Sellary from your Shopify admin again.";

export const SHOPIFY_CONNECT_MESSAGES = {
  ticket_expired: { text: `This connection link has expired. ${OPEN_AGAIN}`, retry: false },
  shop_linked_elsewhere: {
    text: "{shop} is already connected to a different Sellary account. Sign in to that account, or disconnect the store there first.",
    retry: false,
  },
  claim_in_progress: { text: "An earlier attempt to connect {shop} is still finishing. Try again in a few minutes.", retry: true },
  unsupported_currency: { text: "Sellary works with Shopify stores that sell in US dollars. {shop} uses another currency.", retry: false },
  no_fulfilling_location: {
    text: `{shop} has no active location that fulfills online orders. Turn that on for a location in Shopify (Settings → Locations), then open Sellary from your Shopify admin again.`,
    retry: false,
  },
  no_online_store: {
    text: "{shop} doesn't have the Online Store sales channel. Add it in Shopify, then open Sellary from your Shopify admin again.",
    retry: false,
  },
  metafield_conflict: { text: "{shop} already has Sellary product fields Sellary can't use. Contact support.", retry: false },
  closing: { text: "That Shopify account is being disconnected in Sellary. Try again when it finishes.", retry: false },
  shopify_auth: { text: `Sellary has lost access to {shop}. ${OPEN_AGAIN}`, retry: false },
  shopify_unavailable: { text: "Shopify isn't answering. Try again.", retry: true },
  setup_incomplete: { text: "{shop} is connected, but Sellary couldn't finish setting it up. Try again.", retry: true },
};

/** { text, retry } for a refusal reason, with {shop} filled in; unknown reasons read as unavailable. */
export function connectMessage(reason, shop) {
  const m = SHOPIFY_CONNECT_MESSAGES[reason] || SHOPIFY_CONNECT_MESSAGES.shopify_unavailable;
  return { text: m.text.replace(/\{shop\}/g, shop || "your store"), retry: m.retry };
}

/** Same copy for the location picker's refusals. */
export const LOCATION_MESSAGES = {
  not_found: "This account could not be found.",
  not_linked: "This store is not connected. Open Sellary from your Shopify admin to reconnect it.",
  shopify_auth: `Sellary has lost access to this store. ${OPEN_AGAIN}`,
  shopify_unavailable: "Shopify isn't answering.",
  location_not_eligible: "That location can't be used for online orders.",
};

// OPT-344: the "Stock by item location" rows. Refusals of setShopifyLocationMap.
export const MAP_MESSAGES = {
  ...LOCATION_MESSAGES,
  closing: "This account is being closed.",
  invalid_map: "Those locations can't be saved. Check the rows and try again.",
  duplicate_location: "This location is already in the list.",
  location_not_eligible: "That Shopify location can't be used for online orders. Choose another.",
  half_filled: "Fill in both or remove the row.",
  gone: "This Shopify location is no longer available. Choose another or remove the row.",
};

/** Mirrors _shared/shopify/locationMap.cjs: trim, collapse whitespace, upper-case. */
export function normalizeLocationKey(s) {
  return String(s == null ? "" : s).trim().replace(/\s+/g, " ").toUpperCase();
}
